import { faPlus, faTags, faWarning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { AxiosError } from 'axios';
import { createRef, memo, type RefObject, useContext, useEffect, useState } from 'react';

import { Color, ColorBorderLight, ColorVariantsLight } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import PersonContext from '@/pages/Person/PersonContext';
import TagsRepository from '@/repositories/TagsRepository';
import type { Tag } from '@/types/tag';
import { Pill } from '@/ui';
import type { OptionType } from '@/ui/components/Form/Select/types';
import { Select } from '@/ui/Select';
import logAjaxError from '@/utils/logAjaxError';

import Tooltip from '../Tooltip';

import type { Props } from './types';

/**
 * NOTE: This component is business/domain-specific and does not belong in the
 * generic UI primitives library (ui/components). It should be moved to a more
 * appropriate domain-specific location in the codebase.
 */
const TagsApp = (props: Props) => {
  const { contentType } = props;
  const { person, updatePerson } = useContext(PersonContext);
  const personId = person?.id;
  const id = contentType === 'items' ? props.id : personId;
  const { addMessage } = useContext(AppContext);
  const [error, setError] = useState<string | undefined>(undefined);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const ref: RefObject<HTMLDivElement | null> = createRef();
  const repository = TagsRepository();
  /**
   * Send an ajax request to update the tags
   *
   * @param tags The list of tags for a person
   * @returns void
   */
  async function persistTags(tags: Tag[]) {
    try {
      const result = await repository.updateTags({ id, tags, contentType });

      setError(undefined);
      // Reconcile with the server response -- a newly created tag's real slug
      // (taggit slugifies the name server-side) can differ from the raw typed
      // value used as a placeholder before the request completed.
      setSelectedTags(result.tags);
      setAvailableTags(result.available_tags);
    } catch (error) {
      const errorMessage = `Failed to update tags: ${(error as AxiosError).message}`;

      logAjaxError(error);
      setError(errorMessage);
      addMessage('error', errorMessage);
    }
  }

  function setSelectedAndPersistTags(tags: Tag[]) {
    setSelectedTags(tags);

    persistTags(tags);

    if (person && contentType === 'people') {
      void updatePerson();
    }
  }

  useEffect(() => {
    async function loadTags() {
      if (!['people', 'items'].includes(contentType)) {
        console.error(
          'Unknown content type for React Tags App, modify TagsApp#persistTags to add support'
        );
        return;
      }

      try {
        const result = await repository.loadTags({ id, contentType });

        if (result.tags.length) {
          setSelectedTags(result.tags);
        }
        if (result.available_tags.length) {
          setAvailableTags(result.available_tags);
        }
        setError(undefined);
      } catch (error) {
        const errorMessage = `Failed to get tags: ${(error as AxiosError).message}`;

        logAjaxError(error);
        setError(errorMessage);
        addMessage('error', errorMessage);
      }
    }
    if (availableTags.length === 0) {
      loadTags();
    }
    // FIXME: Repositories should be a custom hook or wrapped in a custom hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addMessage, availableTags, contentType, id]);

  const setSelectedOptions = (selectedOptions: OptionType<string>[]) => {
    const selectedTags: Tag[] = selectedOptions.map((option) => {
      const tag = availableTags.find((tag: Tag) => tag.slug === option.value);
      // Options with no matching tag are ones the user just typed and created via the
      // Select's `creatable` affordance -- the backend creates the real tag from its
      // slug/name on save, so this is a placeholder until the next tag list refresh.
      return (
        tag ?? {
          id: option.value,
          name: option.label,
          slug: option.value,
          color: Color.GRAY
        }
      );
    });
    setSelectedAndPersistTags(selectedTags);
    setIsEditing(false);
  };

  const removeTag = (tagSlug: string) => {
    setSelectedAndPersistTags(selectedTags.filter((t) => t.slug !== tagSlug));
  };

  // On escape, cancel editing
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsEditing(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const availableOptions: OptionType<string>[] = availableTags.map((tag: Tag) => ({
    value: tag.slug,
    label: tag.name
  }));

  const selectedOptions: OptionType<string>[] = selectedTags.map((tag) => ({
    value: tag.slug,
    label: tag.name
  }));

  return (
    <div ref={ref} className="flex flex-row items-center flex-wrap gap-1">
      {!isEditing &&
        selectedTags.map((tag, index) => {
          const color: Color = (tag.color as Color) || Color.GRAY;
          const colorClasses = `${ColorVariantsLight[color]} ${ColorBorderLight[color]}`;
          return (
            <Pill
              className={colorClasses}
              key={index}
              onRemove={() => removeTag(tag.slug)}
              removeLabel={`Remove tag ${tag.name}`}
              size="sm"
              variant="subtle">
              {tag.name}
            </Pill>
          );
        })}
      {error && !isEditing && (
        <div className="text-red-500">
          <Tooltip tooltipContent={error}>
            <FontAwesomeIcon icon={faWarning}></FontAwesomeIcon>
          </Tooltip>
        </div>
      )}
      <button
        type="button"
        className={`flex items-center ${isEditing ? 'hidden' : ''}`}
        onClick={() => setIsEditing(true)}>
        <FontAwesomeIcon icon={faPlus} className="w-3.5 h-3.5 text-gray-400"></FontAwesomeIcon>
      </button>
      {isEditing && (
        <Select
          creatable={contentType === 'people'}
          error={error}
          leftIcon={faTags}
          missingOptionsText="No tags available"
          multiple
          onChange={setSelectedOptions}
          options={availableOptions}
          placeholder="Add tag"
          selectedOptions={selectedOptions}
        />
      )}
    </div>
  );
};

export default memo(TagsApp);
